/** Data widgets embedded in blog articles (rendered from post.widgets snapshots). */
import React from "react";
import { LineChart, Line, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid, Legend } from "recharts";

const COLORS = ["#9EE8B4", "#F5E960", "#7DD3FC"];

function GwChart({ data }) {
    const seasons = Object.keys(data?.seasons || {});
    if (!seasons.length) return null;
    const byGw = {};
    seasons.forEach((s) => (data.seasons[s] || []).forEach((r) => {
        byGw[r.gw] = { ...(byGw[r.gw] || { gw: r.gw }), [s]: r.points };
    }));
    const rows = Object.values(byGw).sort((a, b) => a.gw - b.gw);
    return (
        <div className="border border-white/10 bg-[#0d0d0d] p-4 my-4" data-testid="blog-widget-gw-chart">
            <p className="font-mono text-[9px] uppercase tracking-widest text-white/40 mb-2">Body podle gameweeku</p>
            <ResponsiveContainer width="100%" height={220}>
                <LineChart data={rows}>
                    <CartesianGrid stroke="rgba(255,255,255,0.06)" />
                    <XAxis dataKey="gw" stroke="#666" fontSize={10} />
                    <YAxis stroke="#666" fontSize={10} width={28} />
                    <Tooltip contentStyle={{ background: "#111", border: "1px solid #333", fontSize: 11 }} />
                    <Legend wrapperStyle={{ fontSize: 10 }} />
                    {seasons.map((s, i) => (
                        <Line key={s} dataKey={s} stroke={COLORS[i % 3]} dot={false} strokeWidth={2} connectNulls />
                    ))}
                </LineChart>
            </ResponsiveContainer>
        </div>
    );
}

const BUCKET_COLOR = { 1: "text-fa-mint", 2: "text-fa-mint", 3: "text-fa-yellow", 4: "text-red-300", 5: "text-red-300" };

function FdrBuckets({ data }) {
    const buckets = (data?.buckets || []).filter((b) => b);
    if (!buckets.length) return null;
    return (
        <div className="border border-white/10 bg-[#0d0d0d] p-4 my-4" data-testid="blog-widget-fdr">
            <p className="font-mono text-[9px] uppercase tracking-widest text-white/40 mb-2">
                Výkon dle síly soupeře · Ø {data.ppg?.toFixed(2)} PPG{data.diff != null ? ` · L−T ${data.diff > 0 ? "+" : ""}${data.diff.toFixed(2)}` : ""}
            </p>
            <div className="grid grid-cols-2 sm:grid-cols-5 gap-2">
                {buckets.map((b) => (
                    <div key={b.fdr} className="border border-white/10 bg-black/40 p-2">
                        <div className="font-mono text-[8px] uppercase text-white/40">FDR {b.fdr} · {b.games || 0}z</div>
                        <div className={`font-display font-black text-xl tabular-nums ${BUCKET_COLOR[b.fdr]}`}>
                            {b.games ? b.ppg?.toFixed(2) : "—"}
                        </div>
                        {b.games > 0 && (
                            <div className="font-mono text-[8px] text-white/40">haul {Math.round(b.haul_rate ?? 0)}% · blank {Math.round(b.blank_rate ?? 0)}%</div>
                        )}
                    </div>
                ))}
            </div>
        </div>
    );
}

const PHASE_LABELS = { gw1_6: "Start", gw7_12: "GW7-12", gw13_18: "GW13-18", gw19_24: "Vánoce", gw25_30: "GW25-30", gw31_38: "Run-in" };

function PhaseBreakdown({ data }) {
    const seasons = Object.entries(data?.seasons || {});
    if (!seasons.length) return null;
    const ppgOf = (v) => (typeof v === "number" ? v : v?.ppg) || 0;
    return (
        <div className="border border-white/10 bg-[#0d0d0d] p-4 my-4" data-testid="blog-widget-phases">
            <p className="font-mono text-[9px] uppercase tracking-widest text-white/40 mb-2">Fázový rozpad (PPG)</p>
            {seasons.map(([season, phases]) => {
                const max = Math.max(...Object.values(phases || {}).map(ppgOf), 1);
                return (
                    <div key={season} className="mb-2">
                        <div className="font-mono text-[9px] text-white/50 mb-1">{season}</div>
                        <div className="grid grid-cols-6 gap-1">
                            {Object.entries(PHASE_LABELS).map(([k, label]) => {
                                const v = ppgOf(phases?.[k]);
                                return (
                                    <div key={k} className="text-center">
                                        <div className="h-14 flex items-end justify-center">
                                            <div className="w-6 bg-fa-mint/70" style={{ height: `${(v / max) * 100}%` }} />
                                        </div>
                                        <div className="font-mono text-[8px] text-white/40 mt-1">{label}</div>
                                        <div className="font-mono text-[9px] text-white/70 tabular-nums">{v ? v.toFixed(1) : "—"}</div>
                                    </div>
                                );
                            })}
                        </div>
                    </div>
                );
            })}
        </div>
    );
}

function Opponents({ data }) {
    if (!data?.best?.length && !data?.worst?.length) return null;
    const List = ({ title, rows, cls }) => (
        <div>
            <p className={`font-mono text-[9px] uppercase tracking-widest mb-1 ${cls}`}>{title}</p>
            {(rows || []).map((o) => (
                <div key={o.opponent} className="font-mono text-[10px] text-white/70 flex justify-between border-b border-white/5 py-0.5">
                    <span>{o.opponent} <span className="text-white/35">({o.played}z)</span></span>
                    <span className="tabular-nums font-bold">{o.ppg?.toFixed(1)} PPG</span>
                </div>
            ))}
        </div>
    );
    return (
        <div className="border border-white/10 bg-[#0d0d0d] p-4 my-4 grid sm:grid-cols-2 gap-4" data-testid="blog-widget-opponents">
            <List title="Nejoblíbenější soupeři" rows={data.best} cls="text-fa-mint" />
            <List title="Nejtěžší soupeři" rows={data.worst} cls="text-red-300" />
        </div>
    );
}

const WIDGETS = { gw_chart: GwChart, fdr_buckets: FdrBuckets, phase_breakdown: PhaseBreakdown, opponents: Opponents };

export function BlogWidget({ name, widgets }) {
    const base = (name || "").split("__")[0];
    const C = WIDGETS[base];
    const data = widgets?.[name] ?? widgets?.[base];
    if (!C || !data) return null;
    return <C data={data} />;
}
